import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { affiliateAdminApi } from "../lib/affiliateApi";
import { useConfirmModal } from "./adminShared";

const brl = (v) => Number(v || 0).toLocaleString("pt-BR", { style: "currency", currency: "BRL" });
const fmtDate = (d) => d ? new Date(d).toLocaleDateString("pt-BR") : "—";

const TYPE_STYLE = {
    first:     { bg: "bg-amber-500/15 text-amber-200 border-amber-500/30", label: "1º pagamento" },
    second:    { bg: "bg-orange-500/15 text-orange-200 border-orange-500/30", label: "2º pagamento" },
    recurring: { bg: "bg-sky-500/15 text-sky-200 border-sky-500/30", label: "Recorrente" },
};

const STATUS_STYLE = {
    active:    { bg: "bg-emerald-500/15 text-emerald-200 border-emerald-500/30", label: "Ativo" },
    pending:   { bg: "bg-amber-500/15 text-amber-200 border-amber-500/30", label: "Pendente" },
    suspended: { bg: "bg-red-500/15 text-red-200 border-red-500/30", label: "Suspenso" },
    paid:      { bg: "bg-emerald-500/15 text-emerald-200 border-emerald-500/30", label: "Pago" },
};

const Badge = ({ style }) => (
    <span className={`inline-flex items-center gap-1 text-[10px] uppercase tracking-[0.2em] px-2 py-0.5 rounded-full border ${style?.bg || ""}`}>
        {style?.label || "—"}
    </span>
);

const EXPIRY_OPTIONS = [
    { id: "1m", label: "1 mês" },
    { id: "3m", label: "3 meses" },
    { id: "6m", label: "6 meses" },
    { id: "12m", label: "12 meses" },
    { id: "none", label: "Indeterminado" },
];
const REF_CODE_RE = /^[a-z0-9-]+$/;

const ExpiryBadge = ({ affiliate }) => {
    if (!affiliate.expires_at) return null;
    if (affiliate.link_expired) {
        return (
            <span className="inline-flex items-center gap-1 text-[9px] uppercase tracking-wider px-1.5 py-0.5 rounded-full border border-red-500/40 bg-red-500/15 text-red-300">
                <i className="fa-solid fa-triangle-exclamation"></i>Expirado
            </span>
        );
    }
    if (affiliate.link_days_remaining != null && affiliate.link_days_remaining < 30) {
        return (
            <span className="inline-flex items-center gap-1 text-[9px] uppercase tracking-wider px-1.5 py-0.5 rounded-full border border-amber-500/40 bg-amber-500/15 text-amber-300">
                Expira em {affiliate.link_days_remaining}d
            </span>
        );
    }
    return null;
};

const SectionShell = ({ title, subtitle, onBack, children, actions }) => (
    <div className="space-y-5">
        <div className="flex items-center justify-between gap-3">
            <div className="flex items-center gap-3">
                <button
                    data-testid="admin-affiliates-back"
                    onClick={onBack}
                    className="h-10 w-10 rounded-xl border border-white/10 hover:border-amber-500/40 text-slate-400 hover:text-amber-300 flex items-center justify-center transition-all"
                >
                    <i className="fa-solid fa-arrow-left"></i>
                </button>
                <div>
                    <div className="text-[10px] uppercase tracking-[0.3em] text-slate-500">Afiliados</div>
                    <h2 className="salon-heading text-2xl text-white">{title}</h2>
                    {subtitle && <p className="text-xs text-slate-500 mt-0.5">{subtitle}</p>}
                </div>
            </div>
            {actions}
        </div>
        {children}
    </div>
);

// ─── Sec 1 · AFILIADOS ─────────────────────────────────────────────────
const AffiliatesSection = ({ onBack }) => {
    const [items, setItems] = useState([]);
    const [statusFilter, setStatusFilter] = useState("all");
    const [showCreate, setShowCreate] = useState(false);
    const [detail, setDetail] = useState(null);
    const [loading, setLoading] = useState(true);
    const { requestConfirm, ConfirmModalElement } = useConfirmModal();

    const reload = async () => {
        setLoading(true);
        try { setItems(await affiliateAdminApi.listAffiliates(statusFilter) || []); }
        catch (e) { toast.error("Erro ao carregar", { description: e.message }); }
        finally { setLoading(false); }
    };
    useEffect(() => { reload(); }, [statusFilter]); // eslint-disable-line

    const kpis = useMemo(() => ({
        total: items.length,
        active: items.filter((a) => a.status === "active").length,
        pending: items.filter((a) => a.status === "pending").length,
        suspended: items.filter((a) => a.status === "suspended").length,
        month: items.filter((a) => {
            const dt = new Date(a.created_at || 0);
            const now = new Date();
            return dt.getMonth() === now.getMonth() && dt.getFullYear() === now.getFullYear();
        }).length,
    }), [items]);

    return (
        <SectionShell
            title="Afiliados"
            subtitle="Gestão de parceiros de indicação"
            onBack={onBack}
            actions={
                <button
                    data-testid="admin-affiliates-new"
                    onClick={() => setShowCreate(true)}
                    className="rounded-xl bg-amber-500 hover:bg-amber-400 text-slate-950 font-bold px-4 py-2.5 text-sm"
                >
                    <i className="fa-solid fa-plus mr-2"></i>Novo Afiliado
                </button>
            }
        >
            <div className="grid grid-cols-2 sm:grid-cols-5 gap-3">
                {[["Total", kpis.total, "fa-users"], ["Ativos", kpis.active, "fa-user-check"],
                  ["Pendentes", kpis.pending, "fa-clock"], ["Inativos", kpis.suspended, "fa-user-slash"],
                  ["Novos este mês", kpis.month, "fa-user-plus"]].map(([l, v, i]) => (
                    <div key={l} className="rounded-xl border border-white/5 bg-slate-900/40 p-3">
                        <div className="text-[10px] uppercase tracking-[0.25em] text-slate-500 flex items-center gap-1"><i className={`fa-solid ${i}`}></i>{l}</div>
                        <div className="salon-heading text-xl text-white mt-1">{v}</div>
                    </div>
                ))}
            </div>

            <div className="flex gap-2">
                {[["all","Todos"],["active","Ativos"],["pending","Pendentes"],["suspended","Suspensos"]].map(([k, l]) => (
                    <button key={k} onClick={() => setStatusFilter(k)}
                        data-testid={`admin-affiliates-filter-${k}`}
                        className={`text-[11px] uppercase tracking-[0.15em] px-3 py-1.5 rounded-lg transition-all ${statusFilter===k?"bg-amber-500/15 text-amber-200 border border-amber-500/30":"text-slate-500 hover:text-white border border-transparent"}`}>
                        {l}
                    </button>
                ))}
            </div>

            <div className="rounded-2xl border border-white/5 bg-slate-900/40 overflow-x-auto">
                <table className="w-full text-sm">
                    <thead>
                        <tr className="text-[10px] uppercase tracking-[0.25em] text-slate-500 bg-slate-950/60">
                            <th className="text-left py-3 px-4">Afiliado</th>
                            <th className="text-left py-3 px-4">CPF</th>
                            <th className="text-left py-3 px-4">PIX</th>
                            <th className="text-left py-3 px-4">Link</th>
                            <th className="text-right py-3 px-4">Indic.</th>
                            <th className="text-right py-3 px-4">MRR</th>
                            <th className="text-right py-3 px-4">Pending</th>
                            <th className="text-center py-3 px-4">Status</th>
                            <th className="text-right py-3 px-4">Ações</th>
                        </tr>
                    </thead>
                    <tbody>
                        {loading ? (
                            <tr><td colSpan={9} className="text-center py-12 text-slate-500 text-xs">Carregando…</td></tr>
                        ) : items.length === 0 ? (
                            <tr><td colSpan={9} className="text-center py-12 text-slate-500 text-xs">Nenhum afiliado.</td></tr>
                        ) : items.map((a) => (
                            <tr key={a.id} className="border-t border-white/5 hover:bg-slate-800/30" data-testid={`admin-affiliate-row-${a.id}`}>
                                <td className="py-3 px-4">
                                    <div className="text-white font-medium">{a.name}</div>
                                    <div className="text-xs text-slate-500">{a.email}</div>
                                </td>
                                <td className="py-3 px-4 text-xs text-slate-400 font-mono">{a.cpf}</td>
                                <td className="py-3 px-4 text-xs text-slate-400 font-mono">{a.pix_key || "—"}</td>
                                <td className="py-3 px-4 text-xs text-amber-200 font-mono">
                                    <button
                                        onClick={() => { navigator.clipboard.writeText(`https://kinng.app/register?ref=${a.ref_code}`); toast.success("Link copiado"); }}
                                        className="hover:underline"
                                        data-testid={`admin-affiliate-link-${a.id}`}
                                    >
                                        ?ref={a.ref_code}
                                    </button>
                                    <div className="mt-1"><ExpiryBadge affiliate={a} /></div>
                                </td>
                                <td className="py-3 px-4 text-right font-mono text-slate-200">{a.referrals_count}</td>
                                <td className="py-3 px-4 text-right font-mono text-emerald-300">{brl(a.mrr_generated)}</td>
                                <td className="py-3 px-4 text-right font-mono text-amber-300">{brl(a.commission_pending)}</td>
                                <td className="py-3 px-4 text-center"><Badge style={STATUS_STYLE[a.status]} /></td>
                                <td className="py-3 px-4">
                                    <div className="flex items-center justify-end gap-1">
                                        <button data-testid={`admin-affiliate-view-${a.id}`}
                                            onClick={() => setDetail(a)} className="h-8 w-8 rounded-lg border border-white/10 text-slate-400 hover:text-amber-300 hover:border-amber-500/40" title="Ver detalhes">
                                            <i className="fa-solid fa-eye text-xs"></i>
                                        </button>
                                        {a.status === "pending" && (
                                            <button data-testid={`admin-affiliate-approve-${a.id}`}
                                                onClick={async () => { await affiliateAdminApi.approveAffiliate(a.id); toast.success("Aprovado"); reload(); }}
                                                className="h-8 w-8 rounded-lg border border-emerald-500/30 text-emerald-300 hover:bg-emerald-500/10" title="Aprovar">
                                                <i className="fa-solid fa-check text-xs"></i>
                                            </button>
                                        )}
                                        {a.status !== "suspended" && (
                                            <button data-testid={`admin-affiliate-suspend-${a.id}`}
                                                onClick={() => requestConfirm({
                                                    message: `Suspender o afiliado ${a.name}?`,
                                                    confirmLabel: "Suspender",
                                                    danger: true,
                                                    onConfirm: async () => { await affiliateAdminApi.suspendAffiliate(a.id); toast.success("Suspenso"); reload(); },
                                                })}
                                                className="h-8 w-8 rounded-lg border border-red-500/30 text-red-300 hover:bg-red-500/10" title="Suspender">
                                                <i className="fa-solid fa-pause text-xs"></i>
                                            </button>
                                        )}
                                    </div>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>

            {showCreate && <NewAffiliateModal onClose={() => setShowCreate(false)} onCreated={reload} />}
            {detail && (
                <AffiliateDetailDrawer
                    affiliate={detail}
                    onClose={() => setDetail(null)}
                    onUpdated={(patch) => { setDetail((d) => ({ ...d, ...patch })); reload(); }}
                />
            )}
            {ConfirmModalElement}
        </SectionShell>
    );
};

const NewAffiliateModal = ({ onClose, onCreated }) => {
    const [form, setForm] = useState({ name: "", email: "", cpf: "", pix_key: "", password: "", commission_override: null, expiry_option: "none" });
    const [customPct, setCustomPct] = useState(false);
    const [override, setOverride] = useState({ first: 50, second: 50, recurring: 10 });
    const [saving, setSaving] = useState(false);

    const submit = async () => {
        if (!form.name || !form.email || !form.cpf || !form.password) {
            toast.error("Preencha os campos obrigatórios"); return;
        }
        setSaving(true);
        try {
            const body = {
                ...form,
                pix_key: form.pix_key || form.cpf,
                commission_override: customPct ? {
                    first: Number(override.first), second: Number(override.second), recurring: Number(override.recurring),
                } : null,
            };
            const r = await affiliateAdminApi.createAffiliate(body);
            toast.success(`Afiliado criado! Ref: ${r.ref_code}`, { description: `Senha provisória: ${r.provisional_password}` });
            onCreated?.();
            onClose();
        } catch (e) { toast.error("Erro", { description: e.message }); }
        finally { setSaving(false); }
    };

    return (
        <div className="fixed inset-0 bg-black/70 backdrop-blur-sm z-50 flex items-center justify-center p-6" data-testid="admin-affiliate-modal">
            <div className="max-w-lg w-full rounded-2xl border border-white/10 bg-slate-950 p-6 space-y-3 max-h-[90vh] overflow-y-auto">
                <h3 className="salon-heading text-xl text-white">Novo Afiliado</h3>
                {[["name","Nome completo *","text"],["email","E-mail (login do portal) *","email"],["cpf","CPF *","text"],["pix_key","Chave PIX (default: CPF)","text"],["password","Senha provisória *","text"]].map(([k, l, t]) => (
                    <div key={k}>
                        <div className="text-[10px] uppercase tracking-[0.28em] text-slate-500 mb-1">{l}</div>
                        <input data-testid={`admin-affiliate-input-${k}`} type={t} value={form[k]} onChange={(e) => setForm({...form, [k]: e.target.value})}
                            className="w-full bg-slate-900/60 border border-white/10 rounded-lg px-3 py-2 text-sm outline-none focus:border-amber-500/40" />
                    </div>
                ))}
                <label className="flex items-center gap-2 text-xs text-slate-400 cursor-pointer">
                    <input type="checkbox" checked={customPct} onChange={(e) => setCustomPct(e.target.checked)} className="accent-amber-500" data-testid="admin-affiliate-custom-toggle" />
                    Personalizar comissões (padrão: 50/50/10%)
                </label>
                {customPct && (
                    <div className="grid grid-cols-3 gap-2 rounded-xl border border-amber-500/20 bg-amber-500/5 p-3">
                        {[["first","1º"],["second","2º"],["recurring","Rec."]].map(([k, l]) => (
                            <div key={k}>
                                <div className="text-[10px] uppercase tracking-[0.2em] text-slate-500 mb-1">{l} %</div>
                                <input type="number" min="0" max="100" value={override[k]} onChange={(e) => setOverride({...override, [k]: e.target.value})}
                                    className="w-full bg-slate-950/60 border border-white/10 rounded-lg px-2 py-1.5 text-sm font-mono outline-none" />
                            </div>
                        ))}
                    </div>
                )}
                <div>
                    <div className="text-[10px] uppercase tracking-[0.28em] text-slate-500 mb-1">Validade do link</div>
                    <select
                        data-testid="admin-affiliate-expiry-select"
                        value={form.expiry_option}
                        onChange={(e) => setForm({ ...form, expiry_option: e.target.value })}
                        className="w-full bg-slate-900/60 border border-white/10 rounded-lg px-3 py-2 text-sm outline-none focus:border-amber-500/40"
                    >
                        {EXPIRY_OPTIONS.map((o) => (
                            <option key={o.id} value={o.id}>{o.label}</option>
                        ))}
                    </select>
                </div>
                <div className="flex gap-2 pt-3">
                    <button onClick={onClose} className="flex-1 rounded-xl border border-white/10 text-slate-300 py-2.5 text-sm">Cancelar</button>
                    <button data-testid="admin-affiliate-save" onClick={submit} disabled={saving}
                        className="flex-1 rounded-xl bg-amber-500 hover:bg-amber-400 text-slate-950 font-bold py-2.5 text-sm disabled:opacity-40">
                        {saving ? "Criando…" : "Criar afiliado"}
                    </button>
                </div>
            </div>
        </div>
    );
};

const AffiliateDetailDrawer = ({ affiliate, onClose, onUpdated }) => {
    const [commissions, setCommissions] = useState([]);
    const [editingCode, setEditingCode] = useState(false);
    const [codeInput, setCodeInput] = useState(affiliate.ref_code);
    const [expiryOption, setExpiryOption] = useState("none");
    const [savingCode, setSavingCode] = useState(false);
    const [savingExpiry, setSavingExpiry] = useState(false);

    useEffect(() => {
        affiliateAdminApi.affiliateCommissions(affiliate.id).then(setCommissions).catch(() => {});
    }, [affiliate.id]);

    const saveCode = async () => {
        const code = codeInput.trim().toLowerCase();
        if (code.length < 3 || code.length > 30 || !REF_CODE_RE.test(code)) {
            toast.error("Código inválido", { description: "Use apenas letras minúsculas, números e hífens (3 a 30 caracteres)." });
            return;
        }
        setSavingCode(true);
        try {
            const updated = await affiliateAdminApi.updateAffiliate(affiliate.id, { ref_code: code });
            setEditingCode(false);
            onUpdated?.(updated);
            toast.success("Código atualizado");
        } catch (e) {
            toast.error(e.message || "Falha ao salvar código");
        } finally {
            setSavingCode(false);
        }
    };

    const saveExpiry = async () => {
        setSavingExpiry(true);
        try {
            const updated = await affiliateAdminApi.updateAffiliate(affiliate.id, { expiry_option: expiryOption });
            onUpdated?.(updated);
            toast.success("Validade do link atualizada");
        } catch (e) {
            toast.error(e.message || "Falha ao salvar validade");
        } finally {
            setSavingExpiry(false);
        }
    };

    return (
        <div className="fixed inset-0 z-50 flex justify-end" onClick={onClose} data-testid="admin-affiliate-drawer">
            <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" />
            <div className="relative w-full max-w-md bg-slate-950 border-l border-white/10 overflow-y-auto p-6" onClick={(e) => e.stopPropagation()}>
                <button onClick={onClose} className="absolute top-4 right-4 text-slate-400 hover:text-white"><i className="fa-solid fa-xmark"></i></button>
                <div className="text-[10px] uppercase tracking-[0.3em] text-amber-300">Afiliado</div>
                <h3 className="salon-heading text-2xl text-white mb-1">{affiliate.name}</h3>
                <p className="text-xs text-slate-500 mb-4">{affiliate.email} · {affiliate.cpf}</p>
                <div className="space-y-1 text-sm mb-4">
                    <div className="flex justify-between text-xs"><span className="text-slate-500">PIX</span><span className="font-mono">{affiliate.pix_key || "—"}</span></div>
                    <div className="flex justify-between text-xs"><span className="text-slate-500">Indicações</span><span className="font-mono">{affiliate.referrals_count}</span></div>
                    <div className="flex justify-between text-xs"><span className="text-slate-500">MRR gerado</span><span className="font-mono text-emerald-300">{brl(affiliate.mrr_generated)}</span></div>
                    <div className="flex justify-between text-xs"><span className="text-slate-500">Comissão paga</span><span className="font-mono">{brl(affiliate.commission_paid)}</span></div>
                    <div className="flex justify-between text-xs"><span className="text-slate-500">Pendente</span><span className="font-mono text-amber-300">{brl(affiliate.commission_pending)}</span></div>
                </div>

                {/* Ref code — editável pelo super-admin */}
                <div className="rounded-xl border border-white/10 bg-slate-900/40 p-3 mb-3" data-testid="admin-affiliate-detail-ref-code">
                    <div className="flex items-center justify-between mb-1">
                        <span className="text-[10px] uppercase tracking-[0.25em] text-slate-500">Código de indicação</span>
                        {!editingCode && (
                            <button
                                data-testid="admin-affiliate-detail-edit-code"
                                onClick={() => { setCodeInput(affiliate.ref_code); setEditingCode(true); }}
                                className="text-[10px] uppercase tracking-wider text-amber-300/80 hover:text-amber-200"
                            >
                                <i className="fa-solid fa-pen mr-1"></i>Editar
                            </button>
                        )}
                    </div>
                    {editingCode ? (
                        <div className="flex items-center gap-2">
                            <input
                                data-testid="admin-affiliate-detail-code-input"
                                value={codeInput}
                                onChange={(e) => setCodeInput(e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, ""))}
                                maxLength={30}
                                autoFocus
                                className="flex-1 min-w-0 bg-slate-950/60 border border-white/10 rounded-lg px-2 py-1.5 text-sm font-mono text-amber-100 outline-none focus:border-amber-500/40"
                            />
                            <button data-testid="admin-affiliate-detail-save-code" onClick={saveCode} disabled={savingCode}
                                className="shrink-0 rounded-lg bg-amber-500 hover:bg-amber-400 text-slate-950 font-bold px-3 py-1.5 text-xs disabled:opacity-50">
                                {savingCode ? "…" : "Salvar"}
                            </button>
                            <button onClick={() => setEditingCode(false)} disabled={savingCode}
                                className="shrink-0 rounded-lg border border-white/10 text-slate-300 px-3 py-1.5 text-xs disabled:opacity-50">
                                Cancelar
                            </button>
                        </div>
                    ) : (
                        <span className="font-mono text-amber-200 text-sm">{affiliate.ref_code}</span>
                    )}
                </div>

                {/* Validade do link */}
                <div className="rounded-xl border border-white/10 bg-slate-900/40 p-3 mb-6" data-testid="admin-affiliate-detail-expiry">
                    <div className="flex items-center justify-between mb-2">
                        <span className="text-[10px] uppercase tracking-[0.25em] text-slate-500">Validade do link</span>
                        <ExpiryBadge affiliate={affiliate} />
                    </div>
                    <p className="text-xs text-slate-400 mb-2">
                        {affiliate.expires_at
                            ? `Válido até ${new Date(affiliate.expires_at).toLocaleDateString("pt-BR")}`
                            : "Sem prazo de validade"}
                    </p>
                    <div className="flex items-center gap-2">
                        <select
                            data-testid="admin-affiliate-detail-expiry-select"
                            value={expiryOption}
                            onChange={(e) => setExpiryOption(e.target.value)}
                            className="flex-1 bg-slate-950/60 border border-white/10 rounded-lg px-2 py-1.5 text-xs outline-none focus:border-amber-500/40"
                        >
                            {EXPIRY_OPTIONS.map((o) => (
                                <option key={o.id} value={o.id}>{o.label}</option>
                            ))}
                        </select>
                        <button data-testid="admin-affiliate-detail-save-expiry" onClick={saveExpiry} disabled={savingExpiry}
                            className="shrink-0 rounded-lg bg-amber-500 hover:bg-amber-400 text-slate-950 font-bold px-3 py-1.5 text-xs disabled:opacity-50">
                            {savingExpiry ? "…" : "Aplicar"}
                        </button>
                    </div>
                </div>

                <div className="text-[10px] uppercase tracking-[0.3em] text-slate-500 mb-2">Histórico de comissões</div>
                <div className="space-y-2">
                    {commissions.length === 0 ? (
                        <div className="text-xs text-slate-500 text-center py-4">Sem comissões.</div>
                    ) : commissions.map((c) => (
                        <div key={c.id} className="rounded-lg border border-white/5 bg-slate-900/40 p-3">
                            <div className="flex items-center justify-between mb-1">
                                <Badge style={TYPE_STYLE[c.type]} />
                                <Badge style={STATUS_STYLE[c.status]} />
                            </div>
                            <div className="flex justify-between text-xs">
                                <span className="text-slate-400">{c.commission_pct}% · base {brl(c.base_amount)}</span>
                                <span className="font-mono text-emerald-300">{brl(c.amount)}</span>
                            </div>
                        </div>
                    ))}
                </div>
            </div>
        </div>
    );
};

// ─── Sec 2 · COMISSÕES ─────────────────────────────────────────────────
const CommissionsSection = ({ onBack }) => {
    const [items, setItems] = useState([]);
    const [statusFilter, setStatusFilter] = useState("pending");
    const [payTarget, setPayTarget] = useState(null);
    const [pixRef, setPixRef] = useState("");

    const reload = async () => {
        try { setItems(await affiliateAdminApi.listCommissions(statusFilter) || []); }
        catch (e) { toast.error("Erro", { description: e.message }); }
    };
    useEffect(() => { reload(); }, [statusFilter]); // eslint-disable-line

    const pay = async () => {
        try {
            await affiliateAdminApi.payCommission(payTarget.id, { pix_transaction_ref: pixRef });
            toast.success("Pagamento registrado");
            setPayTarget(null); setPixRef(""); reload();
        } catch (e) { toast.error("Erro", { description: e.message }); }
    };

    const totals = useMemo(() => ({
        pending: items.filter((i) => i.status === "pending").reduce((s, i) => s + i.amount, 0),
        paid: items.filter((i) => i.status === "paid").reduce((s, i) => s + i.amount, 0),
    }), [items]);

    return (
        <SectionShell title="Comissões" subtitle="Pagamentos PIX aos afiliados" onBack={onBack}>
            <div className="grid grid-cols-2 gap-3">
                <div className="rounded-xl border border-amber-500/30 bg-amber-500/5 p-4">
                    <div className="text-[10px] uppercase tracking-[0.25em] text-amber-300">A pagar</div>
                    <div className="salon-heading text-2xl text-white mt-1" data-testid="admin-commissions-total-pending">{brl(totals.pending)}</div>
                </div>
                <div className="rounded-xl border border-emerald-500/30 bg-emerald-500/5 p-4">
                    <div className="text-[10px] uppercase tracking-[0.25em] text-emerald-300">Já pago</div>
                    <div className="salon-heading text-2xl text-white mt-1" data-testid="admin-commissions-total-paid">{brl(totals.paid)}</div>
                </div>
            </div>

            <div className="flex gap-2">
                {[["pending","A pagar"],["paid","Pagas"],["all","Todas"]].map(([k, l]) => (
                    <button key={k} onClick={() => setStatusFilter(k)}
                        data-testid={`admin-commissions-filter-${k}`}
                        className={`text-[11px] uppercase tracking-[0.15em] px-3 py-1.5 rounded-lg transition-all ${statusFilter===k?"bg-amber-500/15 text-amber-200 border border-amber-500/30":"text-slate-500 hover:text-white border border-transparent"}`}>
                        {l}
                    </button>
                ))}
            </div>

            <div className="rounded-2xl border border-white/5 bg-slate-900/40 overflow-x-auto">
                <table className="w-full text-sm">
                    <thead>
                        <tr className="text-[10px] uppercase tracking-[0.25em] text-slate-500 bg-slate-950/60">
                            <th className="text-left py-3 px-4">Afiliado</th>
                            <th className="text-left py-3 px-4">Tenant</th>
                            <th className="text-left py-3 px-4">Tipo</th>
                            <th className="text-right py-3 px-4">Valor</th>
                            <th className="text-left py-3 px-4">PIX</th>
                            <th className="text-center py-3 px-4">Status</th>
                            <th className="text-right py-3 px-4">Ação</th>
                        </tr>
                    </thead>
                    <tbody>
                        {items.length === 0 ? (
                            <tr><td colSpan={7} className="text-center py-10 text-xs text-slate-500">Nenhuma comissão.</td></tr>
                        ) : items.map((c) => (
                            <tr key={c.id} className="border-t border-white/5 hover:bg-slate-800/30" data-testid={`admin-commission-row-${c.id}`}>
                                <td className="py-3 px-4 text-white">{c.affiliate_name}</td>
                                <td className="py-3 px-4 text-slate-300 text-xs">{c.tenant_name}</td>
                                <td className="py-3 px-4"><Badge style={TYPE_STYLE[c.type]} /></td>
                                <td className="py-3 px-4 text-right font-mono text-emerald-300">{brl(c.amount)}</td>
                                <td className="py-3 px-4 text-xs text-slate-400 font-mono">{c.affiliate_pix || "—"}</td>
                                <td className="py-3 px-4 text-center"><Badge style={STATUS_STYLE[c.status]} /></td>
                                <td className="py-3 px-4 text-right">
                                    {c.status === "pending" ? (
                                        <button data-testid={`admin-commission-pay-${c.id}`}
                                            onClick={() => setPayTarget(c)}
                                            className="rounded-lg bg-emerald-500 hover:bg-emerald-400 text-slate-950 font-bold px-3 py-1.5 text-xs">
                                            <i className="fa-solid fa-money-bill-transfer mr-1"></i>Pagar
                                        </button>
                                    ) : (
                                        <span className="text-[11px] text-slate-500 font-mono">{c.pix_transaction_ref || fmtDate(c.paid_at)}</span>
                                    )}
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>

            {payTarget && (
                <div className="fixed inset-0 bg-black/70 backdrop-blur-sm z-50 flex items-center justify-center p-6" data-testid="admin-commission-pay-modal">
                    <div className="max-w-md w-full rounded-2xl border border-emerald-500/30 bg-slate-950 p-6 space-y-3">
                        <h3 className="salon-heading text-xl text-white">Registrar pagamento</h3>
                        <div className="rounded-lg border border-white/5 bg-slate-900/40 p-3 text-sm space-y-1">
                            <div className="flex justify-between"><span className="text-slate-500">Afiliado</span><span>{payTarget.affiliate_name}</span></div>
                            <div className="flex justify-between"><span className="text-slate-500">PIX</span><span className="font-mono">{payTarget.affiliate_pix}</span></div>
                            <div className="flex justify-between"><span className="text-slate-500">Valor</span><span className="font-mono text-emerald-300 font-bold">{brl(payTarget.amount)}</span></div>
                        </div>
                        <div>
                            <div className="text-[10px] uppercase tracking-[0.28em] text-slate-500 mb-1">Referência PIX (comprovante)</div>
                            <input value={pixRef} onChange={(e) => setPixRef(e.target.value)}
                                data-testid="admin-commission-pix-ref"
                                placeholder="ex: E23412345678..."
                                className="w-full bg-slate-900/60 border border-white/10 rounded-lg px-3 py-2 text-sm outline-none focus:border-emerald-500/40" />
                        </div>
                        <div className="flex gap-2 pt-2">
                            <button onClick={() => setPayTarget(null)} className="flex-1 rounded-xl border border-white/10 text-slate-300 py-2.5 text-sm">Cancelar</button>
                            <button data-testid="admin-commission-pay-confirm" onClick={pay}
                                className="flex-1 rounded-xl bg-emerald-500 hover:bg-emerald-400 text-slate-950 font-bold py-2.5 text-sm">Confirmar</button>
                        </div>
                    </div>
                </div>
            )}
        </SectionShell>
    );
};

// ─── Sec 3 · PERFORMANCE ────────────────────────────────────────────────
const PerformanceSection = ({ onBack }) => {
    const [data, setData] = useState(null);
    useEffect(() => { affiliateAdminApi.performance().then(setData).catch(() => {}); }, []);
    if (!data) return <SectionShell title="Performance" onBack={onBack}><div className="text-center py-10 text-xs text-slate-500">Carregando…</div></SectionShell>;
    const maxMrr = Math.max(1, ...data.top.map((t) => t.mrr));
    const maxMonth = Math.max(1, ...data.monthly.map((m) => m.total));
    return (
        <SectionShell title="Performance" subtitle="Ranking e evolução de comissões" onBack={onBack}>
            <div className="grid md:grid-cols-2 gap-4">
                <div className="rounded-2xl border border-white/5 bg-slate-900/40 p-5">
                    <div className="text-[10px] uppercase tracking-[0.3em] text-amber-300 mb-3">Top afiliados (MRR gerado)</div>
                    {data.top.length === 0 ? (
                        <div className="text-xs text-slate-500 text-center py-6">Sem indicações ativas ainda.</div>
                    ) : data.top.slice(0, 5).map((t, i) => (
                        <div key={t.affiliate_id} className="mb-3" data-testid={`admin-perf-top-${i}`}>
                            <div className="flex items-baseline justify-between mb-1">
                                <div className="text-sm text-white truncate">{i+1}. {t.name}</div>
                                <div className="text-xs text-emerald-300 font-mono">{brl(t.mrr)}</div>
                            </div>
                            <div className="h-1.5 rounded-full bg-slate-800 overflow-hidden">
                                <div className="h-full bg-amber-400 transition-all" style={{ width: `${(t.mrr / maxMrr) * 100}%` }}></div>
                            </div>
                            <div className="text-[10px] text-slate-500 mt-0.5">{t.tenants} tenant(s) · ref: {t.ref_code}</div>
                        </div>
                    ))}
                </div>
                <div className="rounded-2xl border border-white/5 bg-slate-900/40 p-5">
                    <div className="text-[10px] uppercase tracking-[0.3em] text-sky-300 mb-3">Comissões pagas por mês</div>
                    {data.monthly.length === 0 ? (
                        <div className="text-xs text-slate-500 text-center py-6">Sem comissões pagas nos últimos 6 meses.</div>
                    ) : (
                        <div className="space-y-2">
                            {data.monthly.map((m) => (
                                <div key={m.month} className="flex items-center gap-2 text-xs">
                                    <div className="w-16 text-slate-500 font-mono">{m.month}</div>
                                    <div className="flex-1 h-2 bg-slate-800 rounded overflow-hidden">
                                        <div className="h-full bg-sky-400" style={{ width: `${(m.total / maxMonth) * 100}%` }}></div>
                                    </div>
                                    <div className="w-24 text-right text-sky-300 font-mono">{brl(m.total)}</div>
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            </div>
        </SectionShell>
    );
};

// ─── Sec 4 · SETTINGS ───────────────────────────────────────────────────
const PROGRAM_LINKS = (base) => [
    { id: "signup", title: "Formulário de inscrição de afiliado", desc: "Página onde novos afiliados se cadastram no programa", url: `${base}/afiliado/cadastro` },
    { id: "portal", title: "Portal do afiliado (login)", desc: "Onde afiliados acessam seu painel, link e comissões", url: `${base}/afiliado` },
    { id: "landing-ref", title: "Landing page com ref de exemplo", desc: "Como fica o link de indicação de um afiliado", url: `${base}/?ref=exemplo` },
    { id: "register-ref", title: "Self-signup com ref de exemplo", desc: "Página de cadastro que o cliente do afiliado vê", url: `${base}/register?ref=exemplo` },
];

const SettingsSection = ({ onBack }) => {
    const [settings, setSettings] = useState(null);
    const [history, setHistory] = useState([]);
    const [form, setForm] = useState({ commission_first: 50, commission_second: 50, commission_recurring: 10 });
    const [saving, setSaving] = useState(false);
    const baseUrl = window.location.origin;

    const copyLink = (url) => {
        navigator.clipboard.writeText(url);
        toast.success("Copiado!");
    };

    const load = async () => {
        try {
            const s = await affiliateAdminApi.getSettings();
            setSettings(s);
            setForm({ commission_first: s.commission_first, commission_second: s.commission_second, commission_recurring: s.commission_recurring });
            const h = await affiliateAdminApi.settingsHistory();
            setHistory(h || []);
        } catch (e) { /* ignore */ }
    };
    useEffect(() => { load(); }, []);

    const save = async () => {
        setSaving(true);
        try {
            await affiliateAdminApi.updateSettings({
                commission_first: Number(form.commission_first),
                commission_second: Number(form.commission_second),
                commission_recurring: Number(form.commission_recurring),
            });
            toast.success("Configurações salvas");
            load();
        } catch (e) { toast.error("Erro", { description: e.message }); }
        finally { setSaving(false); }
    };

    return (
        <SectionShell title="Configurações do programa" subtitle="Percentuais padrão de comissão" onBack={onBack}>
            <div className="rounded-2xl border border-white/5 bg-slate-900/40 p-6 max-w-lg">
                {[["commission_first","1º pagamento"],["commission_second","2º pagamento"],["commission_recurring","Recorrente (mensal)"]].map(([k, l]) => (
                    <div key={k} className="mb-4">
                        <div className="text-[10px] uppercase tracking-[0.28em] text-slate-500 mb-1">{l}</div>
                        <div className="flex items-center gap-2">
                            <input type="number" min="0" max="100" step="1"
                                data-testid={`admin-settings-${k}`}
                                value={form[k]} onChange={(e) => setForm({...form, [k]: e.target.value})}
                                className="flex-1 bg-slate-950/60 border border-white/10 rounded-lg px-4 py-2.5 text-2xl font-mono text-white outline-none focus:border-amber-500/40" />
                            <span className="text-2xl text-slate-500">%</span>
                        </div>
                    </div>
                ))}
                <button data-testid="admin-settings-save" onClick={save} disabled={saving}
                    className="w-full rounded-xl bg-amber-500 hover:bg-amber-400 text-slate-950 font-bold py-2.5 text-sm disabled:opacity-40">
                    {saving ? "Salvando…" : "Salvar configurações"}
                </button>
            </div>

            <div className="rounded-2xl border border-white/5 bg-slate-900/40 p-5 max-w-2xl" data-testid="admin-affiliate-links-section">
                <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.3em] text-slate-500 mb-4">
                    <i className="fa-solid fa-link text-amber-400"></i>
                    Links do Programa
                </div>
                <div className="space-y-2.5">
                    {PROGRAM_LINKS(baseUrl).map((l) => (
                        <div key={l.id} data-testid={`admin-affiliate-link-${l.id}`} className="rounded-xl border border-white/5 bg-slate-950/50 p-3.5">
                            <div className="text-xs font-semibold text-white">{l.title}</div>
                            <div className="text-[11px] text-slate-500 mt-0.5 mb-2">{l.desc}</div>
                            <div className="flex items-center gap-2">
                                <div className="flex-1 min-w-0 bg-black/40 border border-white/10 rounded-lg px-3 py-2 font-mono text-xs text-amber-200/90 truncate">
                                    {l.url}
                                </div>
                                <button
                                    data-testid={`admin-affiliate-link-copy-${l.id}`}
                                    onClick={() => copyLink(l.url)}
                                    title="Copiar link"
                                    className="h-9 w-9 rounded-lg border border-white/10 hover:border-amber-500/40 text-slate-400 hover:text-amber-300 flex items-center justify-center transition-all shrink-0"
                                >
                                    <i className="fa-solid fa-copy text-xs"></i>
                                </button>
                            </div>
                        </div>
                    ))}
                </div>
            </div>

            {history.length > 0 && (
                <div className="rounded-2xl border border-white/5 bg-slate-900/40 p-5 max-w-lg">
                    <div className="text-[10px] uppercase tracking-[0.3em] text-slate-500 mb-3">Histórico</div>
                    {history.map((h) => (
                        <div key={h.id} className="border-b border-white/5 last:border-0 py-2 text-xs">
                            <div className="text-slate-400">{new Date(h.changed_at).toLocaleString("pt-BR")} · <span className="text-white">{h.changed_by_name || "admin"}</span></div>
                            <div className="text-slate-500 font-mono">
                                {h.previous.commission_first}/{h.previous.commission_second}/{h.previous.commission_recurring}% →
                                <span className="text-amber-300"> {h.new.commission_first}/{h.new.commission_second}/{h.new.commission_recurring}%</span>
                            </div>
                        </div>
                    ))}
                </div>
            )}
        </SectionShell>
    );
};

// ─── Hub ────────────────────────────────────────────────────────────────
const AdminAffiliates = () => {
    const [view, setView] = useState("hub");
    const [summary, setSummary] = useState({ active: 0, pending_pay: 0, commission_pending: 0 });

    useEffect(() => {
        if (view !== "hub") return;
        (async () => {
            try {
                const [affs, comms] = await Promise.all([
                    affiliateAdminApi.listAffiliates("all").catch(() => []),
                    affiliateAdminApi.listCommissions("pending").catch(() => []),
                ]);
                setSummary({
                    active: (affs || []).filter((a) => a.status === "active").length,
                    pending_pay: (comms || []).length,
                    commission_pending: (comms || []).reduce((s, c) => s + c.amount, 0),
                });
            } catch { /* ignore */ }
        })();
    }, [view]);

    if (view === "affiliates") return <AffiliatesSection onBack={() => setView("hub")} />;
    if (view === "commissions") return <CommissionsSection onBack={() => setView("hub")} />;
    if (view === "performance") return <PerformanceSection onBack={() => setView("hub")} />;
    if (view === "settings") return <SettingsSection onBack={() => setView("hub")} />;

    return (
        <div className="space-y-6" data-testid="admin-affiliates-hub">
            <div>
                <div className="text-[10px] uppercase tracking-[0.3em] text-amber-300">Módulo 04 · plataforma</div>
                <h2 className="salon-heading text-3xl text-white">Programa de Afiliados</h2>
                <p className="text-sm text-slate-500 mt-1">Gestão de parceiros de indicação, comissões PIX e performance de aquisição.</p>
            </div>
            <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
                <HubCard testId="admin-affiliates-card-affiliates" icon="fa-users" title="Afiliados"
                    subtitle={`${summary.active} ativo(s)`} onClick={() => setView("affiliates")} />
                <HubCard testId="admin-affiliates-card-commissions" icon="fa-money-bill-transfer" title="Comissões"
                    subtitle={`${brl(summary.commission_pending)} a pagar`}
                    highlight={summary.pending_pay > 0}
                    onClick={() => setView("commissions")} />
                <HubCard testId="admin-affiliates-card-performance" icon="fa-chart-line" title="Performance"
                    subtitle="Ranking e evolução" onClick={() => setView("performance")} />
                <HubCard testId="admin-affiliates-card-settings" icon="fa-sliders" title="Configurações"
                    subtitle="Percentuais do programa" onClick={() => setView("settings")} />
            </div>
        </div>
    );
};

const HubCard = ({ testId, icon, title, subtitle, onClick, highlight }) => (
    <button data-testid={testId} onClick={onClick}
        className={`text-left rounded-3xl border p-5 transition-all group ${
            highlight ? "border-amber-500/40 bg-amber-500/5 hover:border-amber-500/70"
                      : "border-white/10 bg-slate-900/40 hover:border-amber-500/40"
        }`}>
        <div className="flex items-center justify-between mb-4">
            <div className={`h-12 w-12 rounded-2xl flex items-center justify-center ${highlight ? "bg-amber-500/20" : "bg-amber-500/10"}`}>
                <i className={`fa-solid ${icon} text-2xl text-amber-300`}></i>
            </div>
            <i className="fa-solid fa-arrow-right text-slate-600 group-hover:text-amber-300 group-hover:translate-x-1 transition-all"></i>
        </div>
        <div className="text-white text-lg font-medium mb-1">{title}</div>
        <div className="text-xs text-slate-500">{subtitle}</div>
    </button>
);

export default AdminAffiliates;
